// Peek: the slide-over that shows one record beside the list it came from, so scanning a list
// never loses your place. The frame and its motion live here; the content is the caller's.
// docs/features/peek.md.

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from './Button';
import FadeIn from './FadeIn';
import { IconChevronDown, IconClose, IconEye } from './icons';
import { MS, usePresence } from './motion';
import Tooltip from './Tooltip';

export interface PeekStep {
  /** Where the shown record sits in the list's order, from 0. */
  index: number;
  total: number;
  /** Moves to the previous (-1) or next (1) record. */
  onStep: (dir: 1 | -1) => void;
  /** What one record is called, for the arrows' names: "project" makes "Next project". */
  noun: string;
}

export interface PeekProps {
  open: boolean;
  onClose: () => void;
  /** The dialog's accessible name ("Drone preview: F020-00123"). */
  label: string;
  title: React.ReactNode;
  /** A line under the title: status tags, a code, a subtitle. */
  meta?: React.ReactNode;
  children: React.ReactNode;
  /** Pinned to the panel's bottom edge: the "Open ..." action and anything beside it. */
  footer?: React.ReactNode;
  /** Previous and next arrows with a counter. They open sideways once there are two or more. */
  step?: PeekStep;
  /** Where focus goes on close. Without it, whatever had focus when the peek opened. */
  returnTo?: React.RefObject<HTMLElement | null>;
  width?: 'md' | 'lg';
  /**
   * False for a peek beside a board whose own keys stay live: no blur, no aria-modal, focus
   * stays where it was, and Tab is not held inside.
   */
  modal?: boolean;
  /** The shown record's identity. A change fades the body in afresh and scrolls it to the top. */
  contentKey?: string | number | null;
  /** The body still shows the previous record while the next one loads. */
  busy?: boolean;
}

const WIDTH = { md: 'max-w-[28rem]', lg: 'max-w-[36rem]' } as const;

const TABBABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

/** Open peeks, newest last: Escape belongs to the top one only. */
const stack: number[] = [];
let seq = 0;

// Escape typed into a text field belongs to that field.
const typing = (el: EventTarget | null): boolean => {
  const t = el as HTMLElement | null;
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
};

/**
 * The counter and arrows. They open sideways from nothing when stepping becomes possible and
 * close the same way, keeping the last numbers on the way out.
 */
const StepKeys: React.FC<{ step?: PeekStep }> = ({ step }) => {
  const show = !!step && step.total > 1;
  const { mounted, open } = usePresence(show, MS.exit);
  const last = useRef(step);
  if (show) last.current = step;
  const at = last.current;
  if (!mounted || !at) return null;
  const go = (dir: 1 | -1) => { if (show && step) step.onStep(dir); };
  return (
    <div
      aria-hidden={show ? undefined : true}
      className={`grid transition-[grid-template-columns,opacity] motion-reduce:transition-[opacity] ${
        open ? 'grid-cols-[1fr] opacity-100 duration-standard ease-emphasized' : 'grid-cols-[0fr] opacity-0 duration-exit ease-exit'
      }`}
    >
      {/* The padding keeps the keys' focus rings clear of the clip. */}
      <div className="-my-1 flex min-w-0 items-center gap-0.5 overflow-hidden py-1 pr-0.5">
        <span className="mr-1 whitespace-nowrap text-xs tabular-nums text-on-surface-variant">{at.index + 1} of {at.total}</span>
        <Tooltip label={`Previous ${at.noun}`}>
          <IconButton label={`Previous ${at.noun}`} size="sm" tabIndex={show ? undefined : -1} onClick={() => go(-1)}>
            <IconChevronDown className="h-3.5 w-3.5 rotate-180" />
          </IconButton>
        </Tooltip>
        <Tooltip label={`Next ${at.noun}`}>
          <IconButton label={`Next ${at.noun}`} size="sm" tabIndex={show ? undefined : -1} onClick={() => go(1)}>
            <IconChevronDown className="h-3.5 w-3.5" />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};

export const Peek: React.FC<PeekProps> = (props) => {
  const { open, onClose, returnTo, width = 'md', modal = true } = props;
  const { mounted, open: shown } = usePresence(open, MS.exit);
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const closeKey = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  const latestClose = useRef(onClose);
  latestClose.current = onClose;
  const [id] = useState(() => ++seq);

  // What leaves is what was last shown: the panel keeps its record while it slides out.
  const last = useRef(props);
  if (open) last.current = props;
  const v = last.current;

  // Said aloud on each step, and on opening when focus does not move (a non-modal peek).
  // Set after mount: a live region that mounts already holding its text is not read.
  const [said, setSaid] = useState('');
  const quietOpen = useRef(true);
  const sayIndex = v.step && v.step.total > 1 ? v.step.index : null;
  const sayTotal = v.step?.total;
  useEffect(() => {
    if (!open || !mounted) { setSaid(''); quietOpen.current = true; return; }
    const text = sayIndex === null ? v.label : `${v.label}, ${sayIndex + 1} of ${sayTotal}`;
    if (quietOpen.current) {
      quietOpen.current = false;
      if (modal) return; // focus lands on Close, which reads the dialog's name already
    }
    setSaid(text);
  }, [open, mounted, modal, v.label, sayIndex, sayTotal]);

  // Declared before the focus move below, so on an opening it reads the opener first.
  useEffect(() => {
    if (open) {
      if (!wasOpen.current) opener.current = document.activeElement as HTMLElement | null;
      wasOpen.current = true;
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    // A non-modal peek never took focus, so it hands focus back only when it holds it.
    if (!modal && !panelRef.current?.contains(document.activeElement)) return;
    const back = returnTo?.current || opener.current;
    if (back && back.isConnected) back.focus();
  }, [open, modal, returnTo]);

  useEffect(() => {
    if (open && mounted && modal) closeKey.current?.focus();
  }, [open, mounted, modal]);

  useEffect(() => {
    if (!open) return undefined;
    stack.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || typing(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (stack[stack.length - 1] !== id) return;
      e.preventDefault();
      latestClose.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const at = stack.indexOf(id);
      if (at >= 0) stack.splice(at, 1);
    };
  }, [open, id]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [v.contentKey]);

  // The backdrop blocks the pointer, so a modal peek keeps Tab inside the panel too.
  const onPanelKeyDown = (e: React.KeyboardEvent) => {
    if (!modal || e.key !== 'Tab' || !panelRef.current) return;
    const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(TABBABLE)).filter((el) => el.tabIndex >= 0);
    if (items.length === 0) return;
    const first = items[0];
    const end = items[items.length - 1];
    // A click on plain text focuses the panel itself (tabIndex -1); Shift+Tab from there would leave it.
    const onPanel = document.activeElement === panelRef.current;
    if (e.shiftKey && (document.activeElement === first || onPanel)) { e.preventDefault(); end.focus(); }
    else if (!e.shiftKey && document.activeElement === end) { e.preventDefault(); first.focus(); }
  };

  if (!mounted || typeof document === 'undefined') return null;

  return createPortal(
    <>
      {/* A pair with the panel: the backdrop stays one step below it, whatever either becomes. */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`${modal ? 'scrim' : 'fixed inset-0 bg-[var(--scrim)]'} z-40 transition-opacity ${
          shown ? 'opacity-100 duration-standard ease-emphasized' : 'pointer-events-none opacity-0 duration-exit ease-exit'
        }`}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal={modal ? true : undefined}
        aria-label={v.label}
        aria-hidden={open ? undefined : true}
        tabIndex={-1}
        onKeyDown={onPanelKeyDown}
        className={`fixed inset-y-0 right-0 z-50 flex w-full outline-none ${WIDTH[width]} flex-col bg-surface-container-low shadow-overlay transition-[transform,opacity] motion-reduce:translate-x-0 sm:rounded-l-overlay ${
          shown ? 'translate-x-0 opacity-100 duration-spring ease-spring' : 'translate-x-full opacity-0 duration-exit ease-exit'
        }`}
      >
        <p role="status" className="sr-only">{said}</p>
        <div className="flex flex-none items-start justify-between gap-2 px-5 pb-3 pt-4">
          <div className="min-w-0 flex-1">
            <h2 className="m-0 text-balance text-base font-semibold leading-6 text-on-surface [overflow-wrap:anywhere]">{v.title}</h2>
            {v.meta ? <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-2">{v.meta}</div> : null}
          </div>
          <div className="-mr-1.5 flex flex-none items-center gap-0.5">
            <StepKeys step={v.step} />
            <Tooltip label="Close the preview">
              <IconButton ref={closeKey} label="Close preview" size="sm" onClick={onClose}>
                <IconClose />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-1">
          <div
            aria-busy={v.busy || undefined}
            className={`transition-opacity ${v.busy ? 'opacity-40 duration-exit ease-exit' : 'opacity-100 duration-standard ease-emphasized'}`}
          >
            <FadeIn key={v.contentKey ?? 'record'}>{v.children}</FadeIn>
          </div>
        </div>

        {v.footer ? (
          <div className="flex flex-none items-center gap-2 bg-surface-container px-5 py-3 sm:rounded-bl-overlay">{v.footer}</div>
        ) : null}
      </div>
    </>,
    document.body,
  );
};

/** A titled group inside a peek's body. */
export const PeekSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section aria-label={title}>
    <h3 className="m-0 mb-2 text-[13px] font-medium leading-[18px] text-on-surface-variant">{title}</h3>
    {children}
  </section>
);

/**
 * One labelled value on a line, inside a `<dl>`. A long value wraps rather than truncating:
 * pinch-zoom is blocked app-wide and a title tooltip never reaches touch, so a cut value is lost.
 */
export const PeekFact: React.FC<{ label: string; title?: string; children: React.ReactNode }> = ({ label, title, children }) => (
  <div className="flex min-h-[1.75rem] items-center justify-between gap-3">
    <dt className="flex-none text-[13px] text-on-surface-variant">{label}</dt>
    <dd title={title} className="m-0 min-w-0 text-right text-sm tabular-nums text-on-surface [overflow-wrap:anywhere]">{children}</dd>
  </div>
);

export interface PeekButtonProps {
  /** The record's name, for the accessible name: "Peek at F020-00123". */
  name: string;
  /** Called with the button, which is where focus returns when the peek closes. */
  onPeek: (from: HTMLElement) => void;
  /** The record's id, so stepping can find this row's button again (`findPeekButton`). */
  peekFor?: string | number;
  /** Out of the tab order, for a row that is leaving. */
  inert?: boolean;
  className?: string;
}

/** The eye key on a row. A sibling of the row's link, never inside it, and its click stays its own. */
export const PeekButton: React.FC<PeekButtonProps> = ({ name, onPeek, peekFor, inert, className = '' }) => (
  <Tooltip label="Preview it without leaving the list">
    <IconButton
      label={`Peek at ${name}`}
      size="sm"
      data-peek-for={peekFor}
      tabIndex={inert ? -1 : undefined}
      className={className}
      onClick={(e) => { e.stopPropagation(); onPeek(e.currentTarget); }}
    >
      <IconEye />
    </IconButton>
  </Tooltip>
);

/** The peek key of the row showing `id`, when that row is on screen. */
export const findPeekButton = (id: string | number): HTMLElement | null =>
  typeof document === 'undefined' ? null : document.querySelector<HTMLElement>(`[data-peek-for="${id}"]`);

/** Space on a focused row link opens the peek; Enter keeps opening the page. Only from the link itself. */
export const spaceToPeek = (onPeek: (from: HTMLElement) => void) => (e: React.KeyboardEvent<HTMLElement>) => {
  if (e.key !== ' ' || e.target !== e.currentTarget) return;
  e.preventDefault();
  if (!e.repeat) onPeek(e.currentTarget);
};

export default Peek;
